/*
 * Created on Wed Nov 01 2023 4:27:51 PM
 *
 * ***************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2023
 *
 * All Rights Reserved
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the software and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ****************************************************************************************
 */
import React from "react";
import { Zap, CheckCircle } from "lucide-react";
import { getIcon } from "../utils/iconResolver";
import data from "../data/krumagoData.json";

const ParentWhyKrumago = () => {
  const { whyKrumago } = data;
  const { panel } = whyKrumago;

  return (
    <section className="relative py-16 sm:py-20 lg:py-28 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 via-white to-blue-50/30 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-64 h-64 sm:w-96 sm:h-96 bg-blue-200/30 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 left-0 w-64 h-64 sm:w-96 sm:h-96 bg-purple-200/30 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      <div className="relative max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 xl:gap-20 items-center">

          {/* LEFT — Pillars Grid */}
          <div className="space-y-6 sm:space-y-8">
            <div className="space-y-3 sm:space-y-4">
              <div className="inline-flex items-center gap-2 sm:gap-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-2xl text-sm sm:text-base font-bold shadow-2xl">
                <Zap className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>{whyKrumago.badge}</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 tracking-tight leading-tight">
                {whyKrumago.headline.main}{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                  {whyKrumago.headline.accent}
                </span>{" "}
                {whyKrumago.headline.end}
              </h2>
              <p className="text-lg sm:text-xl text-slate-600 leading-relaxed font-light">
                {whyKrumago.description}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {whyKrumago.pillars.map((pillar, index) => (
                <div
                  key={index}
                  className="group bg-white/80 backdrop-blur-sm border border-slate-200/60 rounded-2xl p-5 sm:p-6 hover:border-transparent transition-all duration-500 transform hover:-translate-y-2 hover:shadow-2xl relative overflow-hidden"
                >
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${pillar.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}
                  />
                  <div
                    className={`inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br ${pillar.gradient} text-white rounded-2xl mb-3 sm:mb-4 transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg`}
                  >
                    {getIcon(pillar.icon, { className: "w-6 h-6 sm:w-8 sm:h-8" })}
                  </div>
                  <h4 className="font-black text-slate-900 mb-2 text-base sm:text-lg group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-purple-600 transition-all duration-500">
                    {pillar.title}
                  </h4>
                  <p className="text-slate-600 text-xs sm:text-sm font-light leading-relaxed">
                    {pillar.description}
                  </p>
                  <div
                    className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${pillar.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left rounded-b-2xl`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — Checklist + Brand Panel */}
          <div className="relative">
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-8 sm:p-10 lg:p-12 text-white shadow-3xl relative overflow-hidden">
              {/* Dot pattern */}
              <div className="absolute inset-0 opacity-5">
                <div
                  className="absolute inset-0"
                  style={{
                    backgroundImage: `radial-gradient(circle at 2px 2px, white 2px, transparent 0)`,
                    backgroundSize: "30px 30px",
                  }}
                />
              </div>

              <div className="relative space-y-6 sm:space-y-8">
                {/* Brand Header */}
                <div className="text-center">
                  <h3 className="text-2xl sm:text-3xl lg:text-4xl font-black mb-3 sm:mb-4">
                    {panel.headline}{" "}
                    <span className="font-serif font-black">
                      <span className="text-[rgb(0,0,168)]">Kru</span>
                      <span className="text-[rgb(227,6,19)]">Ma</span>
                      <span className="text-[rgb(0,0,168)]">go</span>
                      <span className="text-slate-400">?</span>
                    </span>
                  </h3>
                  <p className="text-slate-300 font-light text-sm sm:text-base">{panel.subline}</p>
                </div>

                {/* Checklist */}
                <div className="space-y-3 sm:space-y-4">
                  {panel.checkItems.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 sm:gap-4 group hover:scale-105 transition-transform duration-300"
                    >
                      <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 bg-blue-500/20 rounded-xl flex items-center justify-center group-hover:bg-blue-500/30 transition-colors duration-300">
                        <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
                      </div>
                      <span className="text-white font-semibold text-sm sm:text-base">{item}</span>
                    </div>
                  ))}
                </div>

                {/* Industry Badges */}
                <div className="pt-5 sm:pt-6 border-t border-slate-700/60">
                  <p className="text-slate-400 text-xs sm:text-sm font-medium mb-3 sm:mb-4 text-center">
                    Available for
                  </p>
                  <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
                    {panel.availableFor.map((ind, i) => (
                      <div
                        key={i}
                        className={`flex items-center gap-1.5 sm:gap-2 bg-gradient-to-r ${ind.gradient} px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl text-white text-xs sm:text-sm font-bold shadow-lg`}
                      >
                        {getIcon(ind.icon, { className: "w-4 h-4 sm:w-6 sm:h-6" })}
                        {ind.label}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Decorative glows */}
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-yellow-400/20 rounded-full blur-2xl animate-pulse" />
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-green-400/20 rounded-full blur-2xl animate-pulse delay-1000" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ParentWhyKrumago;